import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  Clock,
  CheckCircle,
  Headphones,
  Mail,
  Sparkles,
  Shield,
} from "lucide-react";
import {
  ticketCategories,
  supportStats,
  TicketCategory,
} from "../data/helpCenterData";
import TicketFormModal from "../components/help/TicketFormModal";
import TicketSuccessModal from "../components/help/TicketSuccessModal";
import {
  TicketPayload,
  useSubmitTicket,
} from "../../dashboard/lib/api/tickets";

interface TicketForm {
  category: string;
  priority: string;
  subject: string;
  description: string;
  email: string;
  attachments: File[];
}

const initialFormData: TicketForm = {
  category: "",
  priority: "medium",
  subject: "",
  description: "",
  email: "",
  attachments: [],
};

const HelpCenter = () => {
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<TicketCategory | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [formData, setFormData] = useState<TicketForm>(initialFormData);
  const [errors, setErrors] = useState<Partial<TicketForm>>({});

  // Use the mutation hook
  const { mutate: submitTicket, isPending: isSubmitting } = useSubmitTicket();

  const handleCategorySelect = (category: TicketCategory) => {
    setSelectedCategory(category);
    setFormData((prev) => ({
      ...prev,
      category: category.id,
      priority: category.priority,
    }));
    setShowTicketForm(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<TicketForm> = {};

    if (!formData.category) {
      newErrors.category = "Please select a category";
    }
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 20) {
      newErrors.description =
        "Please provide more details (at least 20 characters)";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload: TicketPayload = {
      category: formData.category,
      priority: formData.priority,
      subject: formData.subject,
      description: formData.description,
      email: formData.email,
      attachments: formData.attachments,
    };

    submitTicket(payload, {
      onSuccess: (response) => {
        const ticket =
          response.data?.ticketNumber ||
          `APX-${Date.now().toString(36).toUpperCase()}`;
        setTicketNumber(ticket);
        setShowTicketForm(false);
        setIsSubmitted(true);
      },
      onError: () => {
        // Error toast is handled by the hook
        // Keep the form open so user can retry
      },
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setSelectedCategory(null);
    setShowTicketForm(false);
    setIsSubmitted(false);
    setTicketNumber("");
  };

  const handleCloseTicketForm = () => {
    setShowTicketForm(false);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/10 via-white to-[#E05A6A]/10" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B52FF' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#8B52FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#E05A6A]/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-md border border-gray-100 mb-8"
          >
            <Headphones className="w-4 h-4 text-[#8B52FF]" />
            <span className="text-sm font-medium text-gray-700">
              {supportStats.supportAgents} Support Available
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
          >
            How can we{" "}
            <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
              help you?
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto"
          >
            Submit a support ticket for personalized assistance.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Categories */}
            <div className="lg:col-span-2 space-y-8">
              {/* Submit Ticket Header */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Submit a Support Ticket
                </h2>
                <p className="text-gray-600">
                  Choose a category that best describes your issue
                </p>
              </div>

              {/* Category Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                {ticketCategories.map((category, index) => {
                  const Icon = category.icon;
                  return (
                    <motion.button
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleCategorySelect(category)}
                      className={`group relative p-5 rounded-2xl border text-left transition-all hover:shadow-lg ${
                        selectedCategory?.id === category.id
                          ? "border-[#8B52FF] bg-[#8B52FF]/5 shadow-md"
                          : "border-gray-200 bg-white hover:border-[#8B52FF]/50"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                            selectedCategory?.id === category.id
                              ? "bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] text-white"
                              : "bg-gray-100 text-gray-600 group-hover:bg-gradient-to-br group-hover:from-[#8B52FF] group-hover:to-[#E05A6A] group-hover:text-white"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {category.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs text-gray-400">
                              ~{category.responseTime} response
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#8B52FF] group-hover:translate-x-1 transition-all" />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Right Column - Contact Options */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] rounded-2xl p-6 text-white">
                <Sparkles className="w-8 h-8 mb-4 opacity-80" />
                <h3 className="text-xl font-bold mb-2">Need Immediate Help?</h3>
                <p className="text-white/80 text-sm mb-6">
                  Our support team is available 24/7 to assist you with any
                  urgent issues.
                </p>

                <div className="space-y-3">
                  <a
                    href="mailto:support@unimerg.com"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      support@unimerg.com
                    </span>
                  </a>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Tips for Faster Resolution
                </h3>
                <ul className="space-y-3">
                  {[
                    "Include screenshots of any errors",
                    "Provide your account email",
                    "Describe the issue step-by-step",
                    "Mention when the issue started",
                    "Include transaction IDs for payment issues",
                  ].map((tip, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Security Notice */}
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">
                      Security Notice
                    </h4>
                    <p className="text-sm text-amber-700">
                      We will never ask for your password via email or ticket.
                      Keep your credentials secure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticket Form Modal */}
      <AnimatePresence>
        {showTicketForm && !isSubmitted && (
          <TicketFormModal
            isOpen={showTicketForm}
            onClose={handleCloseTicketForm}
            selectedCategory={selectedCategory}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {isSubmitted && (
          <TicketSuccessModal
            isOpen={isSubmitted}
            ticketNumber={ticketNumber}
            email={formData.email}
            selectedCategory={selectedCategory}
            onSubmitAnother={resetForm}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default HelpCenter;
